import _ from '../../utils/underscore'; // eslint-disable-line id-length
import Event from '../../utils/event';
import BaseService from '../../base/base_service';
import { SYSTEM_EVENTS } from '../../events/events';
import IRecentlyPlayedService from './recently_played_service_interface';
import Store from "../../utils/store";

const RECENTLY_PLAYED_LOCAL_STORAGE_KEY= "recently_played_games";

class RecentlyPlayedService extends BaseService implements IRecentlyPlayedService{

  static CLASS: string = 'RecentlyPlayedService';

  //Internal copy of the recently played games list
  private _recentlyPlayedList: number[] = [];
  private _lastUpdateTime: number = null;
  private _refreshInterval: number = 10000;

  init(params) {
    super.init(params);
    this._subscribe();
  }

  //closeGame from GameManagerService triggers this update MANUALLY ( only if the game was in real mode ) since there is no event for this !!!
  private _subscribe(): void {
    this.onSystemEvent(SYSTEM_EVENTS.ON_ORBIT_INIT_COMPLETE, this._onOrbitInitComplete);
    this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._updateRecentlyPlayedGames);
    this.onSystemEvent(SYSTEM_EVENTS.CLOSE_GAME, (data)=> {this._onCloseGame(data.gameType)});
  }

  private _isUpdateRequired(newTime: number): boolean {
    return _.isNull(this._lastUpdateTime) || newTime - this._lastUpdateTime >= this._refreshInterval;
  }

  //Getter for the internal games list -< will result in an update call if the list is undefined
  public getRecentlyPlayedList(): Promise<number[]> {
    if (_.isEmpty(this._recentlyPlayedList) || this._isUpdateRequired(new Date().getTime())) {
      return this._updateRecentlyPlayedGames();
    }
    return Promise.resolve(this._recentlyPlayedList || []);
  }

  private _onCloseGame(gameType: number): void {
    if(!this._isAuthorized()) {
      return;
    }

    let recentlyPlayedListCopy = [...this._recentlyPlayedList];
    let gameTypeIndexInList = recentlyPlayedListCopy.indexOf(gameType);
    if(gameTypeIndexInList !== -1) {
      recentlyPlayedListCopy.splice(gameTypeIndexInList, 1);
    }

    recentlyPlayedListCopy.unshift(gameType);
    this._fireUpdateEvent(recentlyPlayedListCopy);
  }

  private _onOrbitInitComplete() {
    const recentlyPlayedFromLocalStorage = Store.get(RECENTLY_PLAYED_LOCAL_STORAGE_KEY);
    if(recentlyPlayedFromLocalStorage) {
      this._recentlyPlayedList = recentlyPlayedFromLocalStorage.split(",")?.map(Number);
      this._fireUpdateEvent(this._recentlyPlayedList);
    }
  }

  private _updateRecentlyPlayedGames(): Promise<number[]> {
    if (!this._isAuthorized()) {
      return Promise.resolve(this._recentlyPlayedList || []);
    }

    return this._getRecentlyPlayedFromServer().then(recentlyPlayedList => {
      this._fireUpdateEvent(recentlyPlayedList);
      return this._recentlyPlayedList;
    });
  }

  private _getRecentlyPlayedFromServer(): Promise<number[]> {
    return this.serviceFactory.historyService().getRecentlyPlayed()
      .then(data => {
        return data.gameTypes || [];
      })
      .then(recentlyPlayedGames => {
        return this._filterRestrictedGames(recentlyPlayedGames);
      })
      .then(recentlyPlayedGamesFilterRestrictions => {
        return this._filterByLayout(recentlyPlayedGamesFilterRestrictions);
      }).catch(() => []);
  }

  //Updates the internal refference to the recently played list received from the server
  //Fires a RECENTLY_PLAYED_GAMES_UPDATED event to notify the system of the update
  private _fireUpdateEvent(recentlyPlayedList: number[]): void {
    this._lastUpdateTime = new Date().getTime();
    this._recentlyPlayedList = recentlyPlayedList;
    Event.fire(SYSTEM_EVENTS.RECENTLY_PLAYED_GAMES_UPDATED, this._recentlyPlayedList || []);
    Store.set(RECENTLY_PLAYED_LOCAL_STORAGE_KEY, this._recentlyPlayedList || []);
  }

  //Filters the recently games list according to IntegrationService and restrictions
  private _filterRestrictedGames(recentlyPlayedGameTypes): Promise<number[]> {
      return this.serviceFactory.gamesInformationService().getAllGamesInfo()
        .then((gamesInfo) => {
          const result = [];
          for (const gameType of recentlyPlayedGameTypes) {
            if (gamesInfo[gameType]) {
              result.push(gameType);
            }
          }
          return result;
        });
  }

  private _isAuthorized(): boolean {
    return this.serviceFactory.oAuth2Service().isAuthorized();
  }

  private _filterByLayout(recentlyPlayedGameTypes: number[]): Promise<number[]> {
    const result = this.serviceFactory.integrationService().filterInLayoutGames(recentlyPlayedGameTypes);
    if (result) {
      return result;
    }

    return Promise.resolve(recentlyPlayedGameTypes);
  }
}

export default RecentlyPlayedService;